import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { useAuth } from './AuthContext';
import { logEvent } from '../utils/eventsLogger';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const CartContext = createContext(null);

// Composite key: same product in different sizes = different lines (5a).
// Non-flower / no size → falls back to product.id (legacy behavior).
const lineKey = (productId, size) => (size ? `${productId}:${size}` : productId);

// Feb 2026 — Hotfix for "Input should be a valid string" 422 on /api/orders.
// Saved addresses load as structured objects {line1, city, state, postcode,
// apt, ...} but the backend's `OrderCreate.delivery_address` is `str`. We
// serialize at the POST boundary so the structured shape can stay in React
// state (the AddressAutocomplete confirmation card needs the object) while
// the wire format remains the legacy string the backend stores in orders.
export function deliveryAddressToWire(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value !== 'object') return String(value);
  // Structured shape from AddressAutocomplete: { line1, city, state, postcode, apt }
  const apt = (value.apt || '').trim();
  const head = (value.line1 || value.formatted || '').trim();
  const headWithApt = apt ? `${head}, Apt ${apt}` : head;
  const tail = [value.city, value.state, value.postcode].filter(Boolean).join(', ');
  return [headWithApt, tail].filter(Boolean).join(', ');
}

// Phase 5.9.20 — preview mode detector. When an operator (super_admin /
// dispatcher) lands on a customer route with `?preview=1`, all purchase
// mutations are blocked with a clear toast instead of mutating real state.
// Reads from window.location so it works regardless of router context.
const isPreviewMode = () => {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('preview') === '1';
  } catch (_) {
    return false;
  }
};

const previewBlock = (action) => {
  toast.warning(`Preview mode — ${action} disabled. Return to inventory to continue.`, {
    duration: 2500,
    id: 'preview-block',
  });
};

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * addToCart(product, quantity, opts)
   * @param opts { size?: '3.5g'|'7g'|'14g'|'28g', eighths?: number, unitPrice?: number }
   * For flower with a size, eighths + unitPrice come from FLOWER_SIZES.
   * For legacy / non-flower callers, omit opts and we use product.price.
   */
  const addToCart = useCallback((product, quantity = 1, opts = {}) => {
    if (isPreviewMode()) {
      previewBlock('add to cart');
      return;
    }
    const size = opts.size || null;
    const eighths = opts.eighths || 1;
    const unitPrice = typeof opts.unitPrice === 'number' ? opts.unitPrice : product.price;
    const key = lineKey(product.id, size);

    setCartItems((prev) => {
      const existing = prev.find((it) => it.key === key);
      if (existing) {
        return prev.map((it) =>
          it.key === key ? { ...it, quantity: it.quantity + quantity } : it,
        );
      }
      return [...prev, { key, product, size, eighths, unitPrice, quantity }];
    });

    const sizeNote = size ? ` · ${size}` : '';
    try {
      toast.success(`Added to cart · ${product.name}${sizeNote}${quantity > 1 ? ` ×${quantity}` : ''}`, {
        duration: 1800,
        id: `cart-add-${key}`,
      });
    } catch (_) { /* sonner not yet mounted */ }

    // Phase 7.0-lite P0-b — log cart.add (fire-and-forget)
    logEvent('cart.add', {
      product_id: product.id,
      name: product.name,
      category: product.category,
      size: size || null,
      qty: quantity,
      unit_price: unitPrice,
    });
  }, []);

  const removeFromCart = useCallback((key) => {
    setCartItems((prev) => {
      const removed = prev.find((it) => it.key === key);
      if (removed) {
        // Dedupe-keyed so React strict-mode double-invocations of the
        // updater don't emit two events for one removal.
        logEvent('cart.remove', {
          product_id: removed.product?.id,
          name: removed.product?.name,
          size: removed.size || null,
          qty: removed.quantity,
          reason: 'explicit',
        }, { dedupeKey: `cr_${key}_${removed.quantity}` });
      }
      return prev.filter((it) => it.key !== key);
    });
  }, []);

  const updateQuantity = useCallback((key, quantity) => {
    if (quantity <= 0) {
      // Feb 2026 — the qty→0 path is the most common removal flow (customer
      // taps "−" until empty). Previously it filtered the line without
      // emitting `cart.remove`, so the activity feed showed near-zero
      // removals. Emit here to match the explicit-remove path.
      setCartItems((prev) => {
        const removed = prev.find((it) => it.key === key);
        if (removed) {
          logEvent('cart.remove', {
            product_id: removed.product?.id,
            name: removed.product?.name,
            size: removed.size || null,
            qty: removed.quantity,
            reason: 'qty_zeroed',
          }, { dedupeKey: `cr_${key}_zero` });
        }
        return prev.filter((it) => it.key !== key);
      });
      return;
    }
    setCartItems((prev) =>
      prev.map((it) => (it.key === key ? { ...it, quantity } : it)),
    );
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  const cartTotal = cartItems.reduce(
    (sum, it) => sum + it.unitPrice * it.quantity,
    0,
  );
  const cartCount = cartItems.reduce((sum, it) => sum + it.quantity, 0);

  // Phase 5.9 Stage 2 — debounced server-side cart sync so dispatchers can see
  // active carts in real time. Customer-only; fire-and-forget; 600 ms debounce
  // so a burst of qty-edit clicks doesn't hammer the backend.
  const syncTimerRef = useRef(null);
  useEffect(() => {
    if (!user || user.role !== 'customer') return undefined;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      const items = cartItems.map((it) => ({
        product_id: it.product.id,
        name: it.product.name,
        size: it.size || null,
        qty: it.quantity,
        unit_price: it.unitPrice,
      }));
      axios.post(
        `${API_URL}/api/cart/sync`,
        { items, subtotal: cartTotal },
        { withCredentials: true, timeout: 6000 },
      ).catch(() => { /* silent — analytics noise must not surface */ });
    }, 600);
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [cartItems, cartTotal, user]);

  const placeOrder = useCallback(
    async (deliveryAddress, paymentMethod, notes, redeemPts = 0, deliverySlot = null) => {
      if (isPreviewMode()) {
        previewBlock('checkout');
        throw new Error('PREVIEW_MODE');
      }
      if (!user || user.role !== 'customer') {
        throw new Error('Must be logged in as customer');
      }
      setIsLoading(true);
      try {
        const items = cartItems.map((it) => {
          const line = {
            product_id: it.product.id,
            quantity: it.quantity,
          };
          if (it.size) {
            line.size = it.size;
            line.eighths = it.eighths;
            line.unit_price = it.unitPrice;
          }
          return line;
        });
        const body = {
          items,
          delivery_address: deliveryAddressToWire(deliveryAddress),
          payment_method: paymentMethod,
          notes,
        };
        if (redeemPts && Number(redeemPts) > 0) {
          body.redeem_pts = Number(redeemPts);
        }
        if (deliverySlot) body.delivery_slot = deliverySlot;
        try {
          const { data } = await axios.post(`${API_URL}/api/orders`, body, { withCredentials: true });
          clearCart();
          return data;
        } catch (err) {
          // Feb 2026 — Client-side checkout failure capture. Surfaces silent
          // 422/500s in Sentry the moment a customer hits them, with enough
          // context to triage without a screenshot. PII-light: we capture the
          // address SHAPE (string vs object, has-apt) not the address text.
          //
          // Gated by `system.sentry_frontend` flag at the SDK init layer
          // (sentry.js skips init when DSN is unset) — no extra check needed
          // here. If Sentry was never initialised, `captureException` is a
          // safe no-op.
          try {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            const addrShape = (() => {
              if (typeof deliveryAddress === 'string') return 'string';
              if (deliveryAddress && typeof deliveryAddress === 'object') {
                return deliveryAddress.apt ? 'object-with-apt' : 'object';
              }
              return 'empty';
            })();
            Sentry.captureException(err, {
              tags: {
                feature: 'checkout',
                http_status: String(status || 'network'),
                address_shape: addrShape,
                payment_method: paymentMethod || 'unknown',
              },
              contexts: {
                checkout: {
                  item_count: cartItems.length,
                  subtotal: cartItems.reduce((s, it) => s + it.unitPrice * it.quantity, 0),
                  redeem_pts: Number(redeemPts) || 0,
                  payment_method: paymentMethod,
                  wire_address_length: String(body.delivery_address || '').length,
                  has_notes: Boolean(notes && String(notes).trim()),
                  // First Pydantic msg helps cluster identical regressions.
                  pydantic_first_msg: Array.isArray(detail)
                    ? (detail[0]?.msg || null)
                    : (typeof detail === 'string' ? detail : null),
                },
              },
              level: status && status >= 500 ? 'error' : 'warning',
            });
          } catch (_captureErr) {
            // Sentry failure must not mask the original checkout error.
          }
          throw err;
        }
      } finally {
        setIsLoading(false);
      }
    },
    [user, cartItems, clearCart],
  );

  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartCount,
        cartTotal,
        isLoading,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        placeOrder,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
